import { useMutation, gql, useLazyQuery } from '@apollo/client'
import {
  Query,
  Mutation,
  User,
  RetailerAccount,
  FacebookAccount,
  FacebookPage,
  SetAccountFacebookPagesInput,
} from '@promoboxx/graphql-gateway-types'
import * as Facebook from 'fb-sdk-wrapper'
import I18n from 'i18n-js'
import { useEffect, useCallback, useState } from 'react'

import AppConfig from '@src/config/AppConfig'

const FACEBOOK_ACCOUNT_FRAGMENT = gql`
  fragment FacebookAccountFragment on FacebookAccount {
    id
    user_id
    uid
    name
    permissions {
      read_insights
      pages_show_list
      ads_management
      business_management
      pages_read_engagement
      pages_manage_metadata
      pages_manage_ads
      pages_manage_posts
      public_profile
    }
    token_expires_at
    last_auth_error {
      response_body {
        error {
          type
          message
          code
        }
      }
    }
    send_notifications
    created_at
    updated_at
    deleted_at
    token_is_valid
    manageable_pages {
      id
      name
      tasks
      fan_count
      link
      is_published
    }
    profile_url
    picture_url
  }
`

const GET_FACEBOOK_ACCOUNTS = gql`
  ${FACEBOOK_ACCOUNT_FRAGMENT}
  query getFacebookAccountsUseFacebook(
    $resourceType: String!
    $resourceId: ID!
  ) {
    getFacebookAccounts(
      resourceType: $resourceType
      resourceId: $resourceId
      params: { fields: [token_is_valid, manageable_pages] }
    ) {
      id
      ...FacebookAccountFragment
    }
  }
`

const FACEBOOK_PAGE_FRAGMENT = gql`
  fragment FacebookPageFragment on FacebookPage {
    id
    graph_id
    uid
    name
    link
    permissions
    updated_at
    token_expires_at
    last_auth_error {
      response_body {
        error {
          type
          message
          code
        }
      }
    }
    deleted_at
    primary
    likes
    picture_url
    verified
    authenticated_local
    instagram_actor_id
    instagram_business_account_id
    business_manager_permissions
    paid_ads_permissions_enabled
    system_token
    owner_business_name
    owner_business_id
    manual_business_manager_requested_at
    facebook_users_and_roles {
      name
      id
      tasks
      is_active
    }
    automations {
      id
      retailer_id
      category
      automationable_id
      automationable_type
      executed_at
      created_at
      updated_at
      failed_at
      deleted_at
      enabled
      status
    }
    remote_token_status {
      app_id
      type
      application
      data_access_expires_at
      expires_at
      is_valid
      issued_at
      scopes
      granular_scopes {
        scope
        target_ids
      }
      user_id
    }
  }
`

const GET_FACEBOOK_PAGES = gql`
  ${FACEBOOK_PAGE_FRAGMENT}
  query getFacebookPagesUseFacebook(
    $resourceType: String!
    $resourceId: ID!
    $params: FacebookPageParams
  ) {
    getFacebookPages(
      resourceType: $resourceType
      resourceId: $resourceId
      params: $params
    ) {
      id
      ...FacebookPageFragment
    }
  }
`

const CREATE_FACEBOOK_ACCOUNT = gql`
  ${FACEBOOK_ACCOUNT_FRAGMENT}
  mutation createFacebookAccountUseFacebook(
    $user_id: ID!
    $access_token: String!
    $uid: ID!
    $name: String!
    $send_notifications: Boolean
    $permissions: FacebookAccountPermissionsInput
    $inline_auth_facebook_page_id: ID
    $params: FacebookAccountParams
  ) {
    createFacebookAccount(
      user_id: $user_id
      access_token: $access_token
      uid: $uid
      name: $name
      send_notifications: $send_notifications
      permissions: $permissions
      inline_auth_facebook_page_id: $inline_auth_facebook_page_id
      params: $params
    ) {
      id
      ...FacebookAccountFragment
    }
  }
`

const UPDATE_FACEBOOK_ACCOUNT = gql`
  ${FACEBOOK_ACCOUNT_FRAGMENT}
  mutation updateFacebookAccountUseFacebook(
    $id: ID!
    $access_token: String!
    $uid: ID
    $name: String
    $send_notifications: Boolean
    $permissions: FacebookAccountPermissionsInput
    $inline_auth_facebook_page_id: ID
    $params: FacebookAccountParams
  ) {
    updateFacebookAccount(
      id: $id
      access_token: $access_token
      uid: $uid
      name: $name
      send_notifications: $send_notifications
      permissions: $permissions
      inline_auth_facebook_page_id: $inline_auth_facebook_page_id
      params: $params
    ) {
      id
      ...FacebookAccountFragment
    }
  }
`

const DELETE_FACEBOOK_ACCOUNT = gql`
  mutation deleteFacebookAccountUseFacebook($id: ID!) {
    deleteFacebookAccount(id: $id)
  }
`

const DELETE_FACEBOOK_ACCOUNT_PERMISSIONS = gql`
  mutation deleteFacebookAccountPermissionsUseFacebook($id: ID!) {
    deleteFacebookAccountPermissions(id: $id)
  }
`

const CREATE_FACEBOOK_PAGE = gql`
  mutation createFacebookPageUseFacebook(
    $resourceType: String!
    $resourceId: ID!
    $facebookPage: CreateFacebookPageInput!
  ) {
    createFacebookPage(
      resourceType: $resourceType
      resourceId: $resourceId
      facebookPage: $facebookPage
    ) {
      id
    }
  }
`

const UPDATE_FACEBOOK_PAGE = gql`
  mutation updateFacebookPageUseFacebook(
    $id: ID!
    $facebookPage: UpdateFacebookPageInput!
  ) {
    updateFacebookPage(id: $id, facebookPage: $facebookPage) {
      id
    }
  }
`

const DELETE_FACEBOOK_PAGE = gql`
  mutation deleteFacebookPageUseFacebook($id: ID!) {
    deleteFacebookPage(id: $id)
  }
`

const REFRESH_FACEBOOK_PAGE_TOKENS = gql`
  mutation refreshFacebookPageTokensUseFacebook($ids: [ID!]!) {
    refreshFacebookPageTokens(ids: $ids)
  }
`

const SET_ACCOUNT_FACEBOOK_PAGES = gql`
  mutation setAccountFacebookPagesUseFacebook(
    $params: [SetAccountFacebookPagesInput!]!
  ) {
    setAccountFacebookPages(params: $params)
  }
`

const GET_GRAPH_ME = gql`
  query getGraphMeUseFacebook($accessToken: String!) {
    getGraphMe(accessToken: $accessToken) {
      id
      first_name
      middle_name
      last_name
      name
      name_format
      picture {
        data {
          height
          is_silhouuette
          url
          width
        }
      }
      short_name
    }
  }
`

const GET_TOKEN_FROM_CODE = gql`
  query getTokenFromCode($code: String!) {
    getTokenFromCode(code: $code) {
      access_token
      token_type
      expires_in
    }
  }
`

const MANUAL_BUSINESS_REQUEST = gql`
  ${FACEBOOK_PAGE_FRAGMENT}
  query manualBusinessRequestUseFacebook(
    $id: ID!
    $params: FacebookPageParams
  ) {
    manualBusinessRequest(id: $id, params: $params) {
      id
      ...FacebookPageFragment
    }
  }
`

// const GET_GRAPH_ME_PERMISSIONS = gql`
//   query getGraphMePermissionsUseFacebook($accessToken: String!) {
//     getGraphMePermissions(accessToken: $accessToken) {
//       permission
//       status
//     }
//   }
// `

// const GET_GRAPH_TOKEN_STATUS = gql`
//   query getGraphTokenStatusUseFacebook($accessToken: String!) {
//     getGraphTokenStatus(accessToken: $accessToken) {
//       data {
//         app_id
//         type
//         application
//         data_access_expires_at
//         expires_at
//         error {
//           code
//           message
//           type
//         }
//         is_valid
//         issued_at
//         profile_id
//         scopes
//         granular_scopes {
//           scope
//           target_ids
//         }
//         user_id
//       }
//     }
//   }
// `

const PERMISSIONS = {
  public_profile: 'public_profile',
  email: 'email',
  pages_manage_posts: 'pages_manage_posts',
  pages_manage_ads: 'pages_manage_ads',
  pages_manage_metadata: 'pages_manage_metadata',
  read_insights: 'read_insights',
  ads_management: 'ads_management',
  business_management: 'business_management',
  instagram_basic: 'instagram_basic',
  instagram_content_publish: 'instagram_content_publish',
  instagram_manage_insights: 'instagram_manage_insights',
}

interface GranularScopeObject {
  [key: string]: string[]
}

export function useFacebook(user: User, retailerAccount: RetailerAccount) {
  const [loginStatus, setLoginStatus] = useState<facebook.StatusResponse>()
  const [facebookAccounts, setFacebookAccounts] = useState<FacebookAccount[]>()
  const [facebookPages, setFacebookPages] = useState<FacebookPage[]>([])
  const [errors, setErrors] = useState<Error[]>([])

  const [
    getFacebookAccountsQuery,
    getFacebookAccountsResults /*{ data, error, fetchMore, called }*/,
  ] = useLazyQuery<Query>(GET_FACEBOOK_ACCOUNTS, {
    fetchPolicy: 'cache-and-network',
  }) //This call will update the cache, we could add a Query that *would* pull
  // from cache if needed.
  const [
    getFacebookPagesQuery,
    getFacebookPagesResults /*{ data, error, fetchMore, called }*/,
  ] = useLazyQuery<Query>(GET_FACEBOOK_PAGES, {
    fetchPolicy: 'cache-and-network',
  }) //This call will update the cache, we could add a Query that *would* pull
  // from cache if needed.
  const [manualBusinessRequestQuery /*{ data, error, fetchMore, called }*/] =
    useLazyQuery<Query>(MANUAL_BUSINESS_REQUEST)

  const [
    getGraphMeQuery /*{ data, error, fetchMore, called }*/,
    /*getGraphMeResults*/
    ,
  ] = useLazyQuery<Query>(GET_GRAPH_ME)
  const [getTokenFromCodeQuery] = useLazyQuery<Query>(GET_TOKEN_FROM_CODE)
  const [createFacebookAccountMutation] = useMutation<Mutation>(
    CREATE_FACEBOOK_ACCOUNT,
  )
  const [updateFacebookAccountMutation] = useMutation<Mutation>(
    UPDATE_FACEBOOK_ACCOUNT,
  )
  const [deleteFacebookAccountMutation] = useMutation<Mutation>(
    DELETE_FACEBOOK_ACCOUNT,
  )
  const [deleteFacebookAccountPermissionsMutation] = useMutation<Mutation>(
    DELETE_FACEBOOK_ACCOUNT_PERMISSIONS,
  )
  const [createFacebookPageMutation] =
    useMutation<Mutation>(CREATE_FACEBOOK_PAGE)
  const [updateFacebookPageMutation] =
    useMutation<Mutation>(UPDATE_FACEBOOK_PAGE)
  const [deleteFacebookPageMutation] =
    useMutation<Mutation>(DELETE_FACEBOOK_PAGE)
  const [refreshFacebookPageTokensMutation] = useMutation<Mutation>(
    REFRESH_FACEBOOK_PAGE_TOKENS,
  )
  const [setAccountFacebookPagesMutation] = useMutation<Mutation>(
    SET_ACCOUNT_FACEBOOK_PAGES,
  )

  const getManageablePageIds = useCallback(
    (facebookAccountsList: FacebookAccount[] = []) => {
      let manageablePageIds: string[] = []
      if (facebookAccountsList) {
        facebookAccountsList.forEach((facebookAccount) => {
          if (facebookAccount.manageable_pages) {
            facebookAccount.manageable_pages.forEach((manageablePage) => {
              if (
                manageablePage &&
                manageablePageIds.indexOf(manageablePage.id) === -1
              ) {
                manageablePageIds.push(manageablePage.id)
              }
            })
          }
        })
      }
      return manageablePageIds
    },
    [],
  )

  const sortFacebookPages = useCallback(
    (unsortedPages: FacebookPage[]) => {
      let manageablePageIds = getManageablePageIds(facebookAccounts)
      const pages = [...unsortedPages].sort((a, b) => {
        const hasUserControlA =
          (a && manageablePageIds.indexOf(a.graph_id || '') !== -1) || false
        const hasUserControlB =
          (b && manageablePageIds.indexOf(b.graph_id || '') !== -1) || false
        if (hasUserControlA === true && hasUserControlB === false) {
          return -1
        } else if (hasUserControlA === false && hasUserControlB === true) {
          return 1
        } else {
          return 0
        }
      })
      return pages
    },
    [facebookAccounts, getManageablePageIds],
  )

  useEffect(() => {
    setFacebookAccounts(
      (getFacebookAccountsResults?.data
        ?.getFacebookAccounts as FacebookAccount[]) || [],
    )
  }, [getFacebookAccountsResults.data])

  useEffect(() => {
    if (getFacebookPagesResults?.data?.getFacebookPages) {
      setFacebookPages(
        sortFacebookPages(getFacebookPagesResults.data.getFacebookPages),
      )
    }
  }, [getFacebookPagesResults.data, sortFacebookPages])

  const getFacebookAccounts = useCallback(async () => {
    return getFacebookAccountsQuery({
      variables: {
        resourceType: 'users',
        resourceId: user.id,
      },
    })
      .then((response) => {
        setFacebookAccounts(
          (response?.data?.getFacebookAccounts as FacebookAccount[]) || [],
        )
        return response
      })
      .catch((error) => {
        setErrors((errors) => [...(errors || []), error])
      })
  }, [user, getFacebookAccountsQuery])

  const getFacebookPages = useCallback(async () => {
    return getFacebookPagesQuery({
      variables: {
        resourceType: 'retailer_accounts',
        resourceId: retailerAccount.id,
        params: {
          fields: [
            // 'sharable',
            // 'automations',
            // 'auto_share_enabled',
            // 'authenticated_remote',
            // 'automated',
            // 'facebook_users_and_roles',
            // 'alerts',
            'remote_token_status',
          ],
        },
      },
    })
      .then((response) => {
        if (response?.data?.getFacebookPages) {
          setFacebookPages(sortFacebookPages(response.data.getFacebookPages))
        }
        return response
      })
      .catch((error) => {
        setErrors((errors) => [...(errors || []), error])
        return error
      })
  }, [retailerAccount, getFacebookPagesQuery, sortFacebookPages])

  const manualBusinessRequest = useCallback(
    async (id: string) => {
      return manualBusinessRequestQuery({
        variables: {
          id,
          params: {
            fields: [
              // 'sharable',
              // 'automations',
              // 'auto_share_enabled',
              // 'authenticated_remote',
              // 'automated',
              // 'facebook_users_and_roles',
              // 'alerts',
              'remote_token_status',
            ],
          },
        },
      })
        .then((response) => {
          return response?.data?.manualBusinessRequest
        })
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
          return error
        })
    },
    [manualBusinessRequestQuery],
  )

  const getTokenFromCode = useCallback(
    async (code: string) => {
      return getTokenFromCodeQuery({
        variables: {
          code,
        },
      })
        .then((response) => {
          return response?.data?.getTokenFromCode?.access_token
        })
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [getTokenFromCodeQuery],
  )

  const createFacebookAccount = useCallback(
    async (accessToken: string) => {
      //We need some information directly from Facebook for this accessToken...
      return getGraphMeQuery({
        variables: {
          accessToken,
        },
      })
        .then((response) => {
          if (response?.data?.getGraphMe) {
            return createFacebookAccountMutation({
              variables: {
                user_id: user.id,
                access_token: accessToken,
                uid: response?.data?.getGraphMe.id,
                name: response?.data?.getGraphMe.name,
                send_notifications: true,
              },
            })
              .then((response) => response)
              .catch((error) => {
                setErrors((errors) => [...(errors || []), error])
              })
          } else {
            setErrors((errors) => [
              ...(errors || []),
              new Error(I18n.t('facebookPermissionsManager.graphError')),
            ])
            return null
          }
        })
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [user, getGraphMeQuery, createFacebookAccountMutation],
  )

  const updateFacebookAccount = useCallback(
    async (id: string, accessToken: string) => {
      return updateFacebookAccountMutation({
        variables: {
          id,
          access_token: accessToken,
        },
      })
        .then((response) => response)
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [updateFacebookAccountMutation],
  )

  const deleteFacebookAccount = useCallback(
    async (id: string) => {
      return deleteFacebookAccountMutation({
        variables: {
          id,
        },
      })
        .then((response) => response)
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [deleteFacebookAccountMutation],
  )

  const deleteFacebookAccountPermissions = useCallback(
    async (id: string) => {
      return deleteFacebookAccountPermissionsMutation({
        variables: {
          id,
        },
      })
        .then((response) => response)
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [deleteFacebookAccountPermissionsMutation],
  )

  const createFacebookPage = useCallback(
    async (uid: string, graphId: string) => {
      const facebookAccount = facebookAccounts?.find(
        (facebookAccount) => facebookAccount.uid === uid,
      )
      const manageablePage = facebookAccount?.manageable_pages?.find(
        (manageablePage) => manageablePage?.id === graphId,
      )
      return createFacebookPageMutation({
        variables: {
          resourceType: 'retailer_accounts',
          resourceId: retailerAccount.id,
          facebookPage: {
            graph_id: graphId,
            likes: manageablePage?.fan_count,
            link: manageablePage?.link,
            name: manageablePage?.name,
            uid,
          },
        },
      })
        .then((response) => response)
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [retailerAccount, facebookAccounts, createFacebookPageMutation],
  )

  const updateFacebookPage = useCallback(
    async (id: string, facebookPage: FacebookPage) => {
      return updateFacebookPageMutation({
        variables: {
          id,
          facebookPage,
        },
      })
        .then((response) => response)
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [updateFacebookPageMutation],
  )

  const deleteFacebookPage = useCallback(
    async (id: string) => {
      return deleteFacebookPageMutation({
        variables: {
          id,
        },
      })
        .then((response) => response)
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [deleteFacebookPageMutation],
  )

  const refreshFacebookPageTokens = useCallback(
    async (ids: string[]) => {
      return refreshFacebookPageTokensMutation({
        variables: {
          ids,
        },
      })
        .then((response) => response)
        .catch((error) => {
          setErrors((errors) => [
            ...errors.filter((err) => err.message !== error.message),
            error,
          ])
        })
    },
    [refreshFacebookPageTokensMutation],
  )

  const setAccountFacebookPages = useCallback(
    async (params: SetAccountFacebookPagesInput[]) => {
      return setAccountFacebookPagesMutation({
        variables: {
          params,
        },
      })
        .then((response) => response)
        .catch((error) => {
          setErrors((errors) => [...(errors || []), error])
        })
    },
    [setAccountFacebookPagesMutation],
  )

  const getLoginStatus = useCallback(() => {
    return new Promise((resolve, reject) => {
      try {
        Facebook.getGlobalFB().getLoginStatus(
          (response: facebook.StatusResponse) => {
            setLoginStatus(response)
            resolve(response)
          },
        )
      } catch (error) {
        setErrors((errors) => [...(errors || []), error as Error])
        reject(error)
      }
    })
  }, [])

  const login = useCallback(
    (
      authType:
        | 'reauthenticate'
        | 'reauthorize'
        | 'rerequest'
        | undefined = undefined,
    ) => {
      return new Promise(
        (resolve: (value: facebook.StatusResponse) => void, reject) => {
          let options: facebook.LoginOptions =
            AppConfig.FACEBOOK_APP_CONFIGURATION
              ? {
                  config_id: AppConfig.FACEBOOK_APP_CONFIGURATION,
                  response_type: 'code',
                  override_default_response_type: true,
                }
              : {
                  scope: Object.keys(PERMISSIONS).join(','),
                  return_scopes: true, // This only returns on original call, so hard
                  auth_type: authType,
                }
          try {
            Facebook.getGlobalFB().login(
              (response: facebook.StatusResponse) => {
                setLoginStatus(response)
                resolve(response)
              },
              options,
            )
          } catch (error) {
            setErrors((errors) => [...(errors || []), error as Error])
            reject(error)
          }
        },
      )
    },
    [],
  )

  const logout = useCallback(async () => {
    return new Promise((resolve, reject) => {
      try {
        Facebook.getGlobalFB().logout((response: facebook.StatusResponse) => {
          resolve(response)
        })
      } catch (error) {
        reject(error)
      }
    })
  }, [])

  const permissionPertainsToPage = useCallback(
    (targetIds: string[], graphId: string) => {
      if (
        targetIds &&
        (targetIds.length === 0 || targetIds.indexOf(graphId) !== -1)
      ) {
        return true
      } else {
        return false
      }
    },
    [],
  )

  const hasPermissionOrganic = useCallback(
    (granularScopes: GranularScopeObject, graphId: string) => {
      const pagesManagePostsScope =
        granularScopes[PERMISSIONS.pages_manage_posts]

      return !!(
        granularScopes &&
        pagesManagePostsScope &&
        permissionPertainsToPage(pagesManagePostsScope, graphId)
      )
    },
    [permissionPertainsToPage],
  )

  const hasPermissionOrganicInsights = useCallback(
    (granularScopes: GranularScopeObject, graphId: string) => {
      const pagesManagePostsScope =
        granularScopes[PERMISSIONS.pages_manage_posts]
      const pagesManageMetadataScope =
        granularScopes[PERMISSIONS.pages_manage_metadata]

      return !!(
        granularScopes &&
        pagesManagePostsScope &&
        permissionPertainsToPage(pagesManagePostsScope, graphId) &&
        granularScopes &&
        pagesManageMetadataScope &&
        permissionPertainsToPage(pagesManageMetadataScope, graphId)
      )
    },
    [permissionPertainsToPage],
  )

  const hasPermissionAds = useCallback(
    (granularScopes: GranularScopeObject, graphId: string) => {
      const adsManagementScope = granularScopes[PERMISSIONS.ads_management]
      const businessManagementScope =
        granularScopes[PERMISSIONS.business_management]
      const pagesManageAdsScope = granularScopes[PERMISSIONS.pages_manage_ads]

      return !!(
        granularScopes &&
        adsManagementScope &&
        permissionPertainsToPage(adsManagementScope, graphId) &&
        granularScopes &&
        businessManagementScope &&
        permissionPertainsToPage(businessManagementScope, graphId) &&
        granularScopes &&
        pagesManageAdsScope &&
        permissionPertainsToPage(pagesManageAdsScope, graphId)
      )
    },
    [permissionPertainsToPage],
  )

  const hasPermissionAdsInsights = useCallback(
    (granularScopes: GranularScopeObject, graphId: string) => {
      const adsManagementScope = granularScopes[PERMISSIONS.ads_management]
      const businessManagementScope =
        granularScopes[PERMISSIONS.business_management]
      const pagesManageAdsScope = granularScopes[PERMISSIONS.pages_manage_ads]

      return !!(
        granularScopes &&
        adsManagementScope &&
        permissionPertainsToPage(adsManagementScope, graphId) &&
        granularScopes &&
        businessManagementScope &&
        permissionPertainsToPage(businessManagementScope, graphId) &&
        granularScopes &&
        pagesManageAdsScope &&
        permissionPertainsToPage(pagesManageAdsScope, graphId)
      )
    },
    [permissionPertainsToPage],
  )

  const hasPermissionInstagramDesktop = useCallback(
    (granularScopes: GranularScopeObject, graphId: string) => {
      const instagramBasicScope = granularScopes[PERMISSIONS.instagram_basic]
      const instagramContentPublishScope =
        granularScopes[PERMISSIONS.instagram_content_publish]

      return !!(
        granularScopes &&
        instagramBasicScope &&
        permissionPertainsToPage(instagramBasicScope, graphId) &&
        granularScopes &&
        instagramContentPublishScope &&
        permissionPertainsToPage(instagramContentPublishScope, graphId)
      )
    },
    [permissionPertainsToPage],
  )

  const hasPermissionInstagramDesktopInsights = useCallback(
    (granularScopes: GranularScopeObject, graphId: string) => {
      const instagramManageInsightsScope =
        granularScopes[PERMISSIONS.instagram_manage_insights]

      return !!(
        granularScopes &&
        instagramManageInsightsScope &&
        permissionPertainsToPage(instagramManageInsightsScope, graphId)
      )
    },
    [permissionPertainsToPage],
  )

  const isFacebookAccountTokenValid = useCallback(
    (facebookAccount: FacebookAccount | undefined) => {
      return !!facebookAccount && facebookAccount.token_is_valid !== '0'
    },
    [],
  )

  const isFacebookPageTokenValid = useCallback((facebookPage: FacebookPage) => {
    return (
      facebookPage &&
      facebookPage.remote_token_status &&
      facebookPage.remote_token_status.is_valid === true
    )
  }, [])

  interface PermissionStatuses {
    hasPermissionOrganic: boolean
    hasPermissionOrganicInsights: boolean
    hasPermissionAds: boolean
    hasPermissionAdsInsights: boolean
    hasPermissionInstagramDesktop?: boolean
    hasPermissionInstagramDesktopInsights?: boolean
  }

  const getPermissionsStatus = useCallback(
    (page: FacebookPage): PermissionStatuses | null => {
      if (page) {
        const granularScopes: GranularScopeObject = {}
        page.remote_token_status &&
          page.remote_token_status.granular_scopes &&
          page.remote_token_status.granular_scopes.forEach((granularScope) => {
            if (granularScope?.scope) {
              granularScopes[granularScope.scope] =
                (granularScope.target_ids as string[]) || []
            }
          })
        const graphId = page.graph_id as string

        //When instagram dekstop ff is deprecated, we can simplify here.
        return {
          hasPermissionOrganic: hasPermissionOrganic(granularScopes, graphId),
          hasPermissionOrganicInsights: hasPermissionOrganicInsights(
            granularScopes,
            graphId,
          ),
          hasPermissionAds: hasPermissionAds(granularScopes, graphId),
          hasPermissionAdsInsights: hasPermissionAdsInsights(
            granularScopes,
            graphId,
          ),
          hasPermissionInstagramDesktop: hasPermissionInstagramDesktop(
            granularScopes,
            graphId,
          ),
          hasPermissionInstagramDesktopInsights:
            hasPermissionInstagramDesktopInsights(granularScopes, graphId),
        }
      }
      return null
    },
    [
      hasPermissionOrganic,
      hasPermissionOrganicInsights,
      hasPermissionAds,
      hasPermissionAdsInsights,
      hasPermissionInstagramDesktop,
      hasPermissionInstagramDesktopInsights,
    ],
  )

  return {
    getLoginStatus,
    login,
    loginStatus,
    logout,
    getFacebookAccounts,
    getFacebookPages,
    getTokenFromCode,
    createFacebookAccount,
    updateFacebookAccount,
    deleteFacebookAccount,
    deleteFacebookAccountPermissions,
    createFacebookPage,
    updateFacebookPage,
    deleteFacebookPage,
    refreshFacebookPageTokens,
    setAccountFacebookPages,
    facebookAccounts,
    facebookPages,
    errors,
    isFacebookAccountTokenValid,
    isFacebookPageTokenValid,
    getPermissionsStatus,
    getManageablePageIds,
    manualBusinessRequest,
  }
}
